
import { navigateToMap } from 'bits/location'
import { anonymous, blankUser, isRegistered } from 'bits/user_model'
import { Component, Getter, Prop, Vue, namespace } from 'bits/vue'
import OzAvatar from 'library/v4/components/OzAvatar.vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import type { Id, Post, Section, User, WallReactionData } from 'types'
import SurfacePanelPostExpandedTextPanelComments from 'vuecomponents/SurfacePanelPostExpandedTextPanelComments.vue'
import SurfacePostBody from 'vuecomponents/SurfacePostBody.vue'
import SurfacePostSocialContentTopBar from 'vuecomponents/SurfacePostSocialContentTopBar.vue'

@Component({
  components: {
    RelativeTime,
    OzAvatar,
    OzDivider,
    OzIcon,
    SurfacePanelPostExpandedTextPanelComments,
    SurfacePostBody,
    SurfacePostSocialContentTopBar,
  },
})
export default class SurfacePanelPostExpandedTextPanel extends Vue {
  OzDividerColorScheme = OzDividerColorScheme

  @Prop({ default: '' }) attachmentCaption!: string
  @Prop({ default: () => {} }) reactionIdsByPostId!: Record<Id, Id[]>
  @Prop({ default: () => {} }) commentIdsByPostId!: Record<Id, Id[]>

  @Getter title!: string
  @Getter isCommentable!: boolean
  @Getter isReactable!: boolean
  @Getter isSectionable!: boolean
  @Getter reactionData!: WallReactionData
  @Getter xAuthor!: boolean
  @namespace('browser').Getter isSmallerThanDesktop!: boolean
  @namespace('postAction').Getter expandedPost!: Post & { id: Id }
  @namespace('section').Getter sectionById!: (id: Id) => Section | null
  @namespace('userContributor').Getter getUserById!: (id: Id) => User | null

  get post(): Post & { id: Id } {
    return this.expandedPost
  }

  get section(): Section | null {
    if (!this.isSectionable) return null
    return this.post.wall_section_id ? this.sectionById(this.post.wall_section_id) : null
  }

  get author(): Required<User> {
    const foundUser = this.post.author_id ? this.getUserById(this.post.author_id) : null
    return (foundUser || blankUser()) as Required<User>
  }

  get authorName(): string {
    return this.isAuthorRegistered ? this.author.name || this.author.username : anonymous
  }

  get isAuthorRegistered(): boolean {
    return isRegistered(this.author)
  }

  get postBody(): string | undefined {
    return this.post.body
  }

  get xDivider(): boolean {
    // don't show divider if the text panel is completely blank
    return (
      this.xAuthor ||
      !!this.post.subject ||
      !!this.postBody ||
      !!this.post.location_name ||
      !!this.section ||
      !!this.attachmentCaption
    )
  }

  get commentCount(): number {
    return this.post.id ? this.commentIdsByPostId[this.post.id]?.length || 0 : 0
  }

  get xReactions(): boolean {
    // when a user is viewing a wall with reaction disabled and then reaction is enabled + someone react to something
    // the realtime message for new reaction may come before the reaction enable message
    // => wait for reactionData to be available before showing reactions
    return (this.isReactable || this.reactionIdsByPostId[this.post.id]?.length > 0) && !!this.reactionData
  }

  get xComments(): boolean {
    return this.post.published === true && (this.isCommentable || this.commentCount > 0)
  }

  openInMap(): void {
    if (!this.post.location_point) return
    navigateToMap({
      lat: this.post.location_point.latitude,
      lng: this.post.location_point.longitude,
      name: this.post.location_name || '',
    })
  }
}
